import type { ReactNode } from "react";
import { usePostHog } from "posthog-js/react";
import { HeaderInteractionsProvider } from "@/components/shared/header-interactions-provider";
import {
    captureAuthCtaClicked,
    ResponsiveSignInButton,
    SignInPromptSheet,
} from "@/features/account-access";
import {
    getChatControllerState,
    sendTriggerEvent,
} from "@/features/kits-conversations";
import {
    ListingCardProvider,
    type IListingCardInteractions,
} from "@/features/listings";
import {
    isFirstSaveThisSession,
    isIntakeComplete,
    useFirstRunAssistAnalytics,
    useOnboardingGate,
} from "@/features/matching";
import {
    FavoriteButton,
    FavoriteInteractionsProvider,
    type IFavoriteInteractions,
} from "@/features/saved-listings";
import { notifyFavoriteSaved } from "./favorite-save-assistance";
import { cardMeasurementSurface } from "@/features/listings";

function useSaveAssistance() {
    const assistActive = useOnboardingGate();
    const track = useFirstRunAssistAnalytics();
    return () =>
        notifyFavoriteSaved({
            assistActive,
            trackSave: track.save,
            isFirstSaveThisSession,
            isIntakeComplete,
            sendFirstSave: () => sendTriggerEvent({ name: "first_save" }),
        });
}

const favoriteInteractions: Omit<IFavoriteInteractions, "onSaved"> = {
    isChatOpen: () => getChatControllerState().chatOpen,
    captureAuthCtaClicked,
    ResponsiveSignInButton,
    SignInPromptSheet,
};

export function AppShellListingCardProvider({
    children,
}: {
    children: ReactNode;
}) {
    const posthog = usePostHog();
    const onSaved = useSaveAssistance();
    const listingCardInteractions: IListingCardInteractions = {
        renderFavorite: (props) => <FavoriteButton {...props} />,
        onCardOpened: (listingId, presentationId, selectionId, surfaceName) => {
            posthog.capture("dog_card_opened", {
                eventId: crypto.randomUUID(),
                listingId,
                presentationId,
                selectionId,
                surface: cardMeasurementSurface(
                    surfaceName,
                    window.location.pathname,
                ),
                measurementStatus: "ineligible",
            });
        },
    };
    return (
        <HeaderInteractionsProvider
            interactions={{
                onSignIn: () =>
                    captureAuthCtaClicked(posthog, { surface: "header" }),
            }}
        >
            <FavoriteInteractionsProvider
                interactions={{ ...favoriteInteractions, onSaved }}
            >
                <ListingCardProvider interactions={listingCardInteractions}>
                    {children}
                </ListingCardProvider>
            </FavoriteInteractionsProvider>
        </HeaderInteractionsProvider>
    );
}
